import axios from 'axios'
import { Message } from 'element-ui'
import store from '@/store'
import { getTokenBySide } from '@/store/modules/user'

const service = axios.create({
    baseURL: process.env.VUE_APP_BASE_API || '/api',
    timeout: 600000
})

/** 根据请求 URL 推断侧：/admin/** → 后台；其余 → 前台。 */
function detectSide(url) {
    return /^\/admin(\/|$)/.test(url || '') ? 'admin' : 'front'
}

/**
 * 跳转登录页：以**当前路由**为准选择前后台登录（路由能反映用户视角，
 * 比请求 URL 更可靠 —— 后台页面可能也调前台接口）。仅当当前路由无法判定时
 * 才回退到请求 side。
 */
function redirectToLogin(fallbackSide) {
    const here = location.pathname + location.search
    let side = fallbackSide
    if (here.startsWith('/admin')) side = 'admin'
    else if (here.startsWith('/front') || here.startsWith('/login')) side = 'front'

    if (side === 'admin') {
        if (!/^\/admin\/login(\?|$)/.test(here)) {
            location.href = '/admin/login?redirect=' + encodeURIComponent(here)
        }
    } else {
        if (!/^\/login(\?|$)/.test(here)) {
            location.href = '/login?redirect=' + encodeURIComponent(here)
        }
    }
}

// 预览态写守卫：前端 token 以 "prv-" 前缀标识预览会话（与后端 PreviewServiceImpl 一致）。
// 命中预览 + 写方法 → 直接 reject，不发请求；后端 PreviewWriteGuardFilter 是兜底。
const PREVIEW_TOKEN_PREFIX = 'prv-'
const WRITE_METHODS = ['post', 'put', 'delete', 'patch']
// 路径白名单：登出等会话维护类操作不阻塞
const PREVIEW_WRITE_WHITELIST = ['/front/login/logout']

function isPreviewBlocked(config, token) {
    if (!token || !token.startsWith(PREVIEW_TOKEN_PREFIX)) return false
    const m = (config.method || 'get').toLowerCase()
    if (!WRITE_METHODS.includes(m)) return false
    const url = config.url || ''
    return !PREVIEW_WRITE_WHITELIST.some(p => url.indexOf(p) >= 0)
}

// 请求拦截器
service.interceptors.request.use(
    config => {
        const side = detectSide(config.url)
        config.__side = side
        const token = getTokenBySide(side)
        if (token) {
            config.headers['Authorization'] = 'Bearer ' + token
        }
        if (isPreviewBlocked(config, token)) {
            if (!config.silent) Message.warning('预览态下不可执行写操作')
            const err = new Error('预览态下不可执行写操作')
            err.code = 403
            err.bizCode = 'PREVIEW_READONLY'
            return Promise.reject(err)
        }
        return config
    },
    error => {
        return Promise.reject(error)
    }
)

// 响应拦截器
service.interceptors.response.use(
    response => {
        // 菜单/角色版本号感知：后端在每个 /admin/** 响应写 X-Menu-Version。
        // 与本地缓存对比，发现变更则触发权限码重拉（toast 在 action 内单次提示）。
        try {
            const side = response.config.__side || detectSide(response.config.url)
            if (side === 'admin') {
                const v = response.headers && (response.headers['x-menu-version'] || response.headers['X-Menu-Version'])
                if (v) store.dispatch('user/onAdminMenuVersionChange', v)
            }
        } catch (_) { /* 静默：版本号检测失败不影响主流程 */ }

        // 文件下载（blob）直接返回，不走 code 判断
        if (response.config.responseType === 'blob') {
            return response.config.returnResponse ? response : response.data
        }
        const res = response.data
        if (res.code !== 200) {
            const side = response.config.__side || detectSide(response.config.url)
            // 登录过期 / 未登录 → 清状态并跳对应登录页
            if (res.code === 400109 || res.code === 401) {
                if (side === 'admin') store.dispatch('user/adminLogout')
                else store.dispatch('user/logout')
                redirectToLogin(side)
            }
            // silent: true 时由调用方自行处理错误，不全局 toast
            if (!response.config.silent) {
                Message.error(res.msg || '请求失败')
            }
            const err = new Error(res.msg || '请求失败')
            err.code = res.code
            // 业务码（可空字符串，用于按 code 字符串分流前端 toast，如随书光盘详情 4 路异常）
            if (res.bizCode) err.bizCode = res.bizCode
            err.msg = res.msg
            err.data = res.data
            return Promise.reject(err)
        }
        return res
    },
    error => {
        const status = error.response ? error.response.status : 0
        const side = (error.config && error.config.__side) || detectSide(error.config && error.config.url)
        if (status === 401) {
            if (side === 'admin') store.dispatch('user/adminLogout')
            else store.dispatch('user/logout')
            redirectToLogin(side)
        } else {
            Message.error(error.message || '网络异常')
        }
        return Promise.reject(error)
    }
)

export default service
